<template>
  <v-dialog :value="value" persistent @click:outside="close" width="1000">
    <v-card flat color="#fafafa">
      <v-toolbar flat dark class="main">
        <v-toolbar-title>
        Generate Billing Reports
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-col cols="auto" class="px-0">
          <v-btn
            text
            :loading="submitting"
            @click="close">
            Cancel
          </v-btn>
        </v-col>
        <v-col cols="auto" class="mr-2 pl-2">
          <v-btn
            color="primary"
            :loading="submitting"
            :disabled="selectedStatements.length === 0 || !fileGenChecked"
            @click="confirm">
            Confirm
          </v-btn>
        </v-col>
      </v-toolbar>
      <v-card-subtitle class="pb-0 pt-4">
        Select statements to include in aggregated invoices/reporting.
      </v-card-subtitle>
      <v-form ref="form" lazy-validation>
        <v-container fluid class="ma-0 pa-0">
          <v-container fluid class="ma-0 pa-2">
            <div style="display: grid; grid-auto-flow: column;">
              <v-row class="align-center d-flex ml-3">
                <!-- First Column: Search By Field -->
                <v-col cols="4" class="pa-0">
                  <v-autocomplete
                    dense
                    solo
                    flat
                    outlined
                    placeholder="Search By"
                    :items="searchParams"
                    item-text="description"
                    return-object
                    v-model="search.key"
                    style="height: 40px; border-top-right-radius: 0; border-bottom-right-radius: 0;"
                    :menu-props="{ maxHeight: 260, rounded: '' }">
                  </v-autocomplete>
                </v-col>
                <!-- Second Column: Search Value Field -->
                <v-col cols="4" class="pa-0">
                  <template v-if="showDateField">
                    <v-menu
                      v-model="batch_dt_picker"
                      transition="scale-transition"
                      max-width="290px"
                      min-width="290px">
                      <template  v-slot:activator="{ on, attrs }">
                        <v-text-field
                          dense
                          solo
                          flat
                          outlined
                          :placeholder="`Search By ${search.key.description}`"
                          v-bind="attrs"
                          v-on="on"
                          :value="formattedDate" 
                          readonly
                          @keydown.enter="searchStatements"
                          style="height: 40px; border-radius: 0;">
                        </v-text-field>
                      </template>
                      <v-date-picker 
                        v-model="search.value"
                        no-title
                        @input="batch_dt_picker = false">
                      </v-date-picker>
                    </v-menu>
                  </template>
                  <template v-else>
                    <v-text-field
                      dense
                      solo
                      flat
                      outlined
                      :placeholder="
                        search.key
                          ? `Search By ${search.key.description}`
                          : 'Select Search Field'
                        "
                      v-model="search.value"
                      :disabled="!search.key"
                      @keydown.enter="searchStatements"
                      clearable
                      @click:clear="clearSearch"
                      style="height: 40px; border-radius: 0;">
                    </v-text-field>
                  </template>
                </v-col>
                <!-- Third Column: Search Button -->
                <v-col cols="1" class="pa-0">
                  <v-btn
                    dense
                    depressed
                    :loading="searching"
                    :disabled="!search.key || !search.value"
                    style="height: 40px; border-top-left-radius: 0; border-bottom-left-radius: 0; border: 1px solid #9e9e9e; border-left:0px"
                    color="primary"
                    @click="searchStatements">
                    <v-icon>{{ 'mdi-magnify' }}</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
              <div style="display: grid; grid-auto-flow: column;">
                <v-col cols="auto">
                  <v-checkbox 
                    v-model="adminFileChecked"
                    label="Admin"
                    hide-details
                    class="my-auto"
                    :disabled="vendorFileChecked || retailerFileChecked"
                    dense>
                  </v-checkbox>
                </v-col>
                <v-col cols="auto">
                  <v-checkbox 
                    v-model="retailerFileChecked"  
                    label="Retailer"
                    hide-details
                    :disabled="adminFileChecked"
                    class="my-auto"
                    dense>
                  </v-checkbox>
                </v-col>
                <v-col cols="auto">
                  <v-checkbox
                    v-model="vendorFileChecked" 
                    label="Vendor"
                    hide-details
                    :disabled="adminFileChecked"
                    class="my-auto"
                    dense>
                  </v-checkbox>
                </v-col>
              </div>
            </div>
            </v-container>
            <v-divider></v-divider>
            <v-data-table
              dense
              :items-per-page.sync="pageSize"
              :headers="headers"
              :height="tableSize"
              :loading="loading"
              :items.sync="statements"
              :sort-by="sortBy"
              :sort-desc="true"
              v-model="selectedStatements"
              show-select
              v-ripple="false"
              item-key="id"
              :style="{'position': 'relative', 'z-index': '1'}"
              fixed-header
              hide-default-footer>
              <template #[`item.batch_start_date`]="{ item }">
                <span>{{ formatDate(item.batch_start_date) }}</span>
              </template>
              <template #[`item.batch_end_date`]="{ item }">
                <span>{{ formatDate(item.batch_end_date) }}</span>
              </template>
              <template #[`item.updated_on`]="{ item }">
                <span>{{ formatDateTime(item.updated_on) }}</span>
              </template>
            </v-data-table>
          </v-container>
          <!-- Pagination controls -->
        <v-divider></v-divider>
        <v-toolbar flat color="#fafafa">
          <v-row align="center" justify="end">
            <v-col cols="2" style="max-width:150px;">
              <v-select class="mt-8" dense :items="pageSizes" label="Items Per Page" select v-model="pageSize"
                :menu-props="{ top: true, offsetY: true, maxHeight: 500 }">
              </v-select>
            </v-col>
            <v-btn icon large @click="previous()" :disabled="disablePrevious" class="mx-2">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <small>Page {{page}}</small>
            <v-btn icon large @click="next()" :disabled="disableNext" class="mx-2">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </v-row>
        </v-toolbar>
      </v-form>
    </v-card>
    <v-dialog v-model="showConfirm" width="430" persistent>
      <v-card>
        <v-card-title class="pl-5 pr-2 pt-4 pb-2">
          <v-icon class="mr-2" color="warning">mdi-alert</v-icon>
          <div class="title h6">
            Confirm
          </div>
          <v-card-subtitle class="py-2 pl-0 pr-2 ma-0" style="word-break: break-word">
            <p>
              You've selected: <strong>{{ warningFileTypes }}</strong>. 
              For the selected statements, the following reports will be generated and published to Documents Portal:
            </p>
            <p>
              <ul>
                <li v-for="(filename, i) in fileDocList" :key="i">
                  {{  filename }}
                </li>
              </ul>
            </p>
            <div v-if="vendorFileChecked" style="margin-top: 25px">
              <v-alert text type="error" border="left">
                <p class="my-auto">
                  <strong>Warning: This action will load vendor invoices into Quickbooks.</strong>
                </p>
              </v-alert>
            </div>
            <p>Continue?</p>
          </v-card-subtitle>
        </v-card-title>
        <v-card-actions>
          <v-spacer />
          <v-btn color="grey darken-1" text @click="showConfirm = false">
            Cancel
          </v-btn>
          <v-btn color="warning" @click="submit">
            Continue
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>
<script>
// Mixins
import { fullWidth } from "@/mixins/fullWidth";
import { displayAlert } from "@/mixins/displayAlert";
import { format } from "@/mixins/format";
import { userAccess } from "@/mixins/user-access";
import { dateFormat } from '@/mixins/date-format';

export default {
  data() {
    return {
      page: 1,
      pageSize: 50,
      pageSizes: [50, 100],
      name: '',
      filter: '',
      sortBy:'updated_on',
      selectedStatements: [],
      statements: [],
      submitting: false,
      loading: true,
      search: { key: null, value: null },
      searched: false,
      searching: false,
      statementPage: 1,
      batch_dt_picker: false,
      adminFileChecked: false,
      retailerFileChecked: false,
      vendorFileChecked: false,
      showConfirm: false,
      fileDocs: {
        admin: ['Admin Vendor Billing Summary', 'Admin Retailer Credit Report', 'ARG Deduction Report'],
        vendor: ['Vendor Billing Summaries'],
        retailer: ['Weekly Retail Credit Reports']
      }
    }
  },
  name: "ARGStatementGenModal",
  props: ["value"],
  mixins: [displayAlert, fullWidth, format, userAccess, dateFormat],
  async created() {
    const searchKey = this.searchParams.find(sp => sp.param === 'batch_start_date')
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const threeWeeksAgo = new Date(today)
    threeWeeksAgo.setDate(today.getDate() - 21)
    
    const day = threeWeeksAgo.getDay()
    const diff = threeWeeksAgo.getDate() - day + (day === 0 ? -6 : 1)
    
    const monday = new Date(threeWeeksAgo.setDate(diff))
    this.search = {
      key: searchKey,
      value: monday.toISOString().split('T')[0]
    }
    this.searched = true
    await this.getStatements()
  },
  watch: {
    pageSize: {
      handler () {
        if (this.page > 1) this.page = 1
        this.getStatements()
      }
    },
  'search.key': {
    handler(newVal, oldVal) {
      if (oldVal) {
        this.search.value = null
        this.getStatements()
      }
    },
    immediate: false
  }
  },
  computed: {
    headers() {
      return [
      { sortable: false, filterable: false, value: 'data-table-select', class: 'accent' },
      { text: 'Statement Name', class:'accent white--text',  value: 'name' },
      { text: 'Year', class:'accent white--text', value: 'billing_year' },
      { text: 'Period', class:'accent white--text', value: 'billing_period' },
      { text: 'Week', class:'accent white--text', value: 'billing_week' },
      { text: 'Batch Start Date', class:'accent white--text',  value: 'batch_start_date' },
      { text: 'Batch End Date', class:'accent white--text',  value: 'batch_end_date' },
      { text: 'Updated On', class:'accent white--text',  value: 'updated_on' },
    ]
    },
    fileGenChecked() {
      return (this.adminFileChecked || this.retailerFileChecked || this.vendorFileChecked)
    },
    offset () {
      return (this.page - 1) * this.pageSize
    },
    disablePrevious () {
      return this.offset === 0
    },
    disableNext () {
      return this.statements.length < this.pageSize
    },
    searchParams () {
      return [
        { description: 'Batch Start Date', param: 'batch_start_date' }
      ]
    },
    showDateField () {
      return this.search.key?.param === 'batch_start_date' || this.search.key?.param === 'created_on'
    },
    formattedDate() {
      return this.search.value ? this.formatDate(this.search.value) : ''
    },
    warningFileTypes() {
      if (this.adminFileChecked) {
        return 'Admin'
      }
      const types = []
      if (this.vendorFileChecked) {
        types.push('Vendor')
      }
      if (this.retailerFileChecked) {
        types.push('Retailer')
      }
      return types.join(' and ')
    },
    fileDocList() {
      let listItems = []
      if (this.adminFileChecked) {
        listItems = [...this.fileDocs.admin]
      } else {
        if (this.vendorFileChecked) {
          listItems.push(...this.fileDocs.vendor)
        }
        if (this.retailerFileChecked) {
          listItems.push(...this.fileDocs.retailer)
        }
      }
      return listItems
    }
  },
  methods: {
    close() {
      this.$emit('closeModal')
    },
    previous () {
      this.page = this.page - 1
      if (this.page < 1) {
        this.page = 1
      }
      if (this.searched) {
        this.searchStatements()
      } else {
        this.getStatements()
      }
    },
    next () {
      this.page = this.page + 1
      if (this.searched) {
        this.searchStatements()
      } else {
        this.getStatements()
      }
    },
    syncStatementSelections(newStatements) {
      return newStatements.map(statement => {
        const selectedStatement = this.selectedStatements.find(s => s.id === statement.id)
        return {
          ...statement,
          selected: !!selectedStatement
        }
      })
    },
    async getStatements () {
      if (this.searched) return this.searchStatements()
      this.loading = true
      const params = this.buildStatementListParams()
      let statements = []
      try {
        const res = await this.$Statements.getStatementList(params)
        if (res?.data?.length > 0) {
          statements = res.data
        }
        this.statements = this.syncStatementSelections(statements)
      } catch (err) {
        this.handleError(err)
      } finally {
        this.loading = false
      }
    },
    async searchStatements () {
      this.loading = true
      this.searching = true
      let key = this.search.key?.param
      let value = this.search.value
      if (!this.searched) {
        this.statementPage = this.page
        this.page = 1
      }
      const params = this.buildStatementListParams()
      if (key && value) {
        params[`${key}`] = value
      }
      let statements = []
      try {
        const res = await this.$Statements.search(params, this.pageSize, this.offset)
        if (res?.data?.length > 0) {
          statements = res.data
        }
        statements = this.syncStatementSelections(statements)
        this.statements = statements
      } catch (err) {
        this.handleError(err)
      } finally {
        this.searching = false
        this.loading = false
        this.searched = true
      }
    },
    clearSearch() {
      this.page = 1
      this.filter = ''
      this.searched = false
      this.getStatements()
    },
    buildStatementListParams () {
      const params = {
        status: 'FINALIZED'
      }
      if (!this.searched) {
        params.limit = this.pageSize
        params.offset = this.offset
      }
      if (this.limitAccessByUserParties) {
        params.party_ids = [...this.userBatchPartyIds]
      }
      return params
    },
    confirm() {
      this.showConfirm = true
    },
    submit() {
      this.showConfirm = false
      this.submitARGStatementFileGen()
    },
    async submitARGStatementFileGen() {
      this.submitting = true
      const selectedIds = this.selectedStatements.map(s => s.statement_id)
      const { billing_year, billing_period, billing_week, batch_start_date } = this.selectedStatements[0] // batch start date selection requirement guarantees consistency here

      const name = `${[billing_year, billing_period, billing_week, batch_start_date].join('_').replaceAll(' ', '_')}`

      const params = {
        statement_name: name,
        statement_ids: selectedIds
      }
      
      Object.assign(params, {
        admin: this.adminFileChecked,
        retailer: this.retailerFileChecked,
        vendor: this.vendorFileChecked
      });

      try {
        await this.$Statements.submitARGStatementFileGen(params)
        this.emitAlert(true, 'success', `Successfully submitted ${name.replaceAll('_', ' ')}`)
        this.$emit('closeProcessModal')
      } catch (err) {
        this.handleError(err)
      } finally {
        this.submitting = false
      }
    }
  },
};
</script>
<style>
.v-data-table-header .v-data-table__checkbox.v-simple-checkbox .v-input--selection-controls__input .v-icon {
  color: #fff !important;
}
</style>
